import { useState } from "react";
import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import { ArrowRight, ArrowUp, PencilLine, X } from "lucide-react";
import Loader from "../PromptKit/Loader";

const cn = (...inputs) => twMerge(clsx(inputs));

const FollowUpBar = ({ followUp, onOptionSelect, onSkip }) => {
  const [customResponse, setCustomResponse] = useState("");
  const isCustomActive = !!customResponse.trim();

  const submitCustomResponse = (event) => {
    event.preventDefault();
    const response = customResponse.trim();
    if (!response) return;
    onOptionSelect({ label: response, response });
  };

  return (
    <section
      className="mx-auto mb-3 w-full max-w-3xl overflow-hidden rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-[0_10px_30px_rgba(24,29,39,0.06)]"
      aria-label="Follow-up question"
    >
      <div className="flex items-center justify-between gap-3 px-4 py-3.5">
        <h3 className="min-w-0 text-base font-bold leading-6 text-gray-800 dark:text-gray-100">
          {followUp.question}
        </h3>
        <button
          type="button"
          aria-label="Dismiss question"
          className="shrink-0 rounded-md p-1 text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-700 dark:hover:text-gray-200 transition focus:outline-none"
          onClick={onSkip}
        >
          <X size={15} aria-hidden="true" />
        </button>
      </div>

      <div
        className={cn(
          "divide-y divide-gray-100 dark:divide-gray-800 border-y border-gray-100 dark:border-gray-800 transition-[filter,opacity] duration-200",
          isCustomActive && "opacity-35 blur-[1px]",
        )}
      >
        {followUp.options.map((option, index) => (
          <button
            key={`${option.label}-${option.response}-${index}`}
            type="button"
            disabled={isCustomActive}
            className="group flex min-h-11 w-full items-center gap-3 px-4 py-2.5 text-left transition hover:bg-secondary-light-transparent dark:hover:bg-secondary/20 focus:outline-none focus-visible:bg-secondary-light-transparent"
            onClick={() => onOptionSelect(option)}
          >
            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-gray-100 dark:bg-gray-800 text-xs font-medium text-gray-500 dark:text-gray-400 transition group-hover:bg-white dark:group-hover:bg-gray-700 group-hover:text-secondary dark:group-hover:text-secondary-light">
              {index + 1}
            </span>
            <span className="min-w-0 flex-1 break-words text-sm text-gray-700 dark:text-gray-200">
              {option.label}
            </span>
            <ArrowRight
              size={15}
              className="shrink-0 text-gray-300 dark:text-gray-600 opacity-0 transition group-hover:translate-x-0.5 group-hover:text-secondary dark:group-hover:text-secondary-light group-hover:opacity-100"
              aria-hidden="true"
            />
          </button>
        ))}
      </div>

      <form
        onSubmit={submitCustomResponse}
        className={cn(
          "flex items-center gap-2 bg-gray-50/60 dark:bg-gray-800/40 px-3 py-2 transition",
          isCustomActive && "m-2 rounded-xl bg-gray-100 dark:bg-gray-800 shadow-inner",
        )}
      >
        <span
          className={cn(
            "flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-gray-400 transition",
            isCustomActive && "bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-200 shadow-xs",
          )}
        >
          <PencilLine size={14} aria-hidden="true" />
        </span>
        <input
          value={customResponse}
          onChange={(event) => setCustomResponse(event.target.value)}
          placeholder="Something else"
          aria-label="Write another answer"
          className="h-8 min-w-0 flex-1 bg-transparent px-2 text-sm leading-5 text-gray-700 dark:text-gray-100 caret-secondary outline-none placeholder:text-gray-400 dark:placeholder:text-gray-500"
        />
        {isCustomActive ? (
          <button
            type="submit"
            aria-label="Send answer"
            className="flex h-8 w-8 items-center justify-center rounded-lg bg-secondary text-white shadow-xs transition hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-secondary/30"
          >
            <ArrowUp size={16} strokeWidth={2.5} />
          </button>
        ) : (
          <button
            type="button"
            className="rounded-lg bg-gray-100 dark:bg-gray-800 px-3 py-1.5 text-sm font-medium text-gray-600 dark:text-gray-300 transition hover:bg-gray-200 dark:hover:bg-gray-700 hover:text-gray-800 dark:hover:text-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-300"
            onClick={onSkip}
          >
            Skip
          </button>
        )}
      </form>
    </section>
  );
};

const BaseChat = ({
  messages,
  input,
  setInput,
  onSend,
  loading,
  conversationRef,
  streamingMessageIdRef,
  Header,
  Prompts,
  Message,
  Input,
  inputProps = {},
  inputDisabled = loading,
  headerProps = {},
  messageProps = {},
  Loading = Loader,
  loadingProps = {},
  className = "",
  contentClassName = "",
  userImage,
  userName,
}) => {
  const [dismissedFollowUp, setDismissedFollowUp] = useState(null);

  const lastMessage = messages[messages.length - 1];
  const activeFollowUp =
    !loading &&
      lastMessage &&
      lastMessage.role !== "user" &&
      Array.isArray(lastMessage.follow_up?.options) &&
      lastMessage.follow_up.options.length > 0 &&
      lastMessage.follow_up !== dismissedFollowUp
      ? lastMessage.follow_up
      : null;

  const handleOptionSelect = (option) => {
    // Send the machine-readable response; show the Q&A pair in the user bubble.
    onSend(
      option.response,
      `Q: ${activeFollowUp.question}\nA: ${option.label}`,
    );
  };

  return (
    <div
      className={cn(
        "flex flex-col p-3 rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900/60 h-full min-h-0 font-sans overflow-hidden",
        className,
      )}
    >
      <div className="flex flex-col h-full w-full min-h-0 overflow-hidden">
        <div
          ref={conversationRef}
          className={`flex flex-col gap-4 items-center flex-1 min-h-0 overflow-y-auto scrollbar-hide pb-3 ${contentClassName || "xl:pt-20"}`}
        >
          {Header && <Header {...headerProps} />}

          {Prompts && <Prompts setInput={setInput} getChat={onSend} />}

          <div className="flex flex-col gap-5 px-1 w-full mt-4">
            {messages.map((msg, index) => {
              const hasLaterUserMessage = messages
                .slice(index + 1)
                .some((laterMessage) => laterMessage.role === "user");

              return (
                <Message
                  key={msg.id || index}
                  message={msg}
                  streamingMessageId={streamingMessageIdRef?.current}
                  userImage={userImage}
                  userName={userName}
                  showFollowUp={!hasLaterUserMessage}
                  {...messageProps}
                />
              );
            })}

  {loading && messages[messages.length - 1]?.role === "user" && (
    <div className="w-full">
      <Loading {...loadingProps} />
    </div>
  )}
          </div>
        </div>

        <div className="flex-shrink-0 mt-auto pt-4">
          {activeFollowUp && (
            <FollowUpBar
              key={activeFollowUp.question}
              followUp={activeFollowUp}
              onOptionSelect={handleOptionSelect}
              onSkip={() => setDismissedFollowUp(activeFollowUp)}
            />
          )}
          {Input && (
            <div className="pb-2">
              <Input
                {...inputProps}
                input={input}
                setInput={setInput}
                onSend={onSend}
                disabled={inputDisabled}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BaseChat;
